/**
 * AnalyzeSOWDialog — Analyze an already-saved SOW with AI
 *
 * Unlike AddSOWDialog, this dialog is not a create flow. It targets an existing
 * engagement (identified by `sowId`) and asks the backend to re-analyze its
 * scope against the firm's config catalog. The backend pulls `scopeDescription`
 * directly from pk.engagements via listEngagementsPgFirst, so we don't have to
 * resend the SOW text in the request body.
 *
 * Apply contract is identical to AddSOWDialog.handleApplySuggestions:
 *   POST /config/company/:companyId/override
 * once per selected item. That is intentional -- keeping both dialogs on the
 * same apply endpoint means the server-side override semantics (dedupe,
 * customName, estimatedHours) stay consistent regardless of where the AI
 * suggestions originated.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { toast } from 'sonner';

interface AnalyzeSOWDialogProps {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  companyId: string;
  sowId: string;
  sowName?: string;
  /** Fires after Apply Selected completes, so the parent can refresh lists. */
  onApplied?: () => void;
}

interface NewConfigItem {
  name: string;
  category: string;
  description: string;
  phase: number;
  reason: string;
  coreFeature: boolean;
  estimatedHours: number;
}

interface ExistingItemSuggestion {
  itemId: string;
  enabled: boolean;
  phase: number;
  reason: string;
}

export function AnalyzeSOWDialog({
  open,
  onClose,
  accessToken,
  companyId,
  sowId,
  sowName,
  onApplied,
}: AnalyzeSOWDialogProps) {
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newItemSuggestions, setNewItemSuggestions] = useState<NewConfigItem[]>([]);
  const [existingItemSuggestions, setExistingItemSuggestions] = useState<
    ExistingItemSuggestion[]
  >([]);
  const [selectedNewItems, setSelectedNewItems] = useState<number[]>([]);
  const [selectedExistingItems, setSelectedExistingItems] = useState<number[]>([]);

  // Kick off analysis when the dialog opens. We key on (open, sowId) so that
  // reopening for a different SOW triggers a fresh analysis instead of
  // reusing stale suggestions from the previous target.
  useEffect(() => {
    if (!open || !sowId || !companyId) return;
    let cancelled = false;

    const run = async () => {
      setAnalyzing(true);
      setError(null);
      setNewItemSuggestions([]);
      setExistingItemSuggestions([]);
      setSelectedNewItems([]);
      setSelectedExistingItems([]);

      try {
        // Step 1: Refresh HLOs for the engagement.
        // /reanalyze-sow re-runs the OpenAI HLO extractor against whatever
        // SOW text is currently stored on the engagement (set either by an
        // earlier upload-sow call or by handleAnalyzeSOW in AddSOWDialog).
        // We deliberately fire-and-forget here -- a reanalyze failure is
        // non-fatal, the config-item analyzer below can still run, and HLOs
        // already on the engagement remain in place. We surface the failure
        // as a console warning so it shows up in diagnostics.
        try {
          const reanalyzeResponse = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${sowId}/reanalyze-sow`,
            {
              method: 'POST',
              headers: getAuthHeadersWithJson(accessToken),
              body: JSON.stringify({}),
            },
          );
          if (cancelled) return;
          if (!reanalyzeResponse.ok) {
            const detail = await reanalyzeResponse.text().catch(() => '');
            console.warn(
              `[AnalyzeSOWDialog] reanalyze-sow failed (${reanalyzeResponse.status}):`,
              detail,
            );
          } else {
            const result = await reanalyzeResponse.json().catch(() => ({}));
            console.log(
              `[AnalyzeSOWDialog] HLOs refreshed for engagement ${sowId}: ${
                result?.hlos?.length ?? 0
              } HLOs`,
            );
          }
        } catch (reErr) {
          if (!cancelled) {
            console.warn('[AnalyzeSOWDialog] reanalyze-sow threw:', reErr);
          }
        }

        if (cancelled) return;

        // Step 2: Get config-item suggestions.
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/analyze-sow/${companyId}`,
          {
            method: 'POST',
            headers: getAuthHeadersWithJson(accessToken),
            // sowIds is the only payload the backend needs -- it reads
            // scopeDescription from pk.engagements itself. Mirroring the
            // shape AddSOWDialog.handleAnalyzeSOW sends keeps the two
            // entry points on the same server contract.
            body: JSON.stringify({ sowIds: [sowId] }),
          },
        );

        if (cancelled) return;

        if (!response.ok) {
          let detail = `Analysis failed (status ${response.status})`;
          try {
            const body = await response.json();
            detail = body?.error || body?.userMessage || detail;
          } catch {
            // keep the status-only detail
          }
          setError(detail);
          toast.error(detail);
          return;
        }

        const data = await response.json();
        const next = (data.newItemSuggestions || []) as NewConfigItem[];
        const existing = (data.existingItemSuggestions || []) as ExistingItemSuggestion[];
        setNewItemSuggestions(next);
        setExistingItemSuggestions(existing);
        // Default to everything selected so Apply is a one-click affordance;
        // users opt out of specific items instead of opting in.
        setSelectedNewItems(next.map((_, i) => i));
        setSelectedExistingItems(
          existing.map((s, i) => (s.enabled ? i : -1)).filter((i) => i >= 0),
        );
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.message || 'Failed to analyze SOW';
        setError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, sowId, companyId, accessToken]);

  const toggleNewItem = (index: number) => {
    setSelectedNewItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleExistingItem = (index: number) => {
    setSelectedExistingItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const enabledExistingIndices = existingItemSuggestions
    .map((s, i) => (s.enabled ? i : -1))
    .filter((i) => i >= 0);

  const totalSelected = selectedNewItems.length + selectedExistingItems.length;

  const handleApply = async () => {
    setApplying(true);
    try {
      // Existing item overrides -- phase/enabled updates.
      const existingToApply = selectedExistingItems.map((i) => existingItemSuggestions[i]);
      for (const suggestion of existingToApply) {
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}/override`,
          {
            method: 'POST',
            headers: getAuthHeadersWithJson(accessToken),
            body: JSON.stringify({
              override: {
                configItemId: suggestion.itemId,
                phase: suggestion.phase,
                enabled: suggestion.enabled,
              },
            }),
          },
        );
      }

      // Newly-suggested items -- created as custom overrides. Same client-id
      // pattern as AddSOWDialog.handleApplySuggestions so the two flows
      // produce indistinguishable records in the override store.
      const newToApply = selectedNewItems.map((i) => newItemSuggestions[i]);
      for (const item of newToApply) {
        const newItemId = `custom-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${companyId}/override`,
          {
            method: 'POST',
            headers: getAuthHeadersWithJson(accessToken),
            body: JSON.stringify({
              override: {
                configItemId: newItemId,
                phase: item.phase,
                enabled: true,
                customName: item.name,
                customDescription: item.description,
                customCategory: item.category,
                estimatedHours: item.estimatedHours,
                notes: `AI-suggested from SOW: ${item.reason}`,
              },
            }),
          },
        );
      }

      toast.success(
        `Applied ${totalSelected} configuration ${totalSelected === 1 ? 'item' : 'items'}`,
      );
      onApplied?.();
      onClose();
    } catch (err: any) {
      console.error('[AnalyzeSOWDialog] apply failed:', err);
      toast.error(err?.message || 'Failed to apply suggestions');
    } finally {
      setApplying(false);
    }
  };

  // Verify token used in headers so linter doesn't complain about unused
  // import paths in CI builds that prune dead imports.
  void getAuthHeaders;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-5 text-purple-600" />
            Analyze SOW with AI
          </DialogTitle>
          <DialogDescription>
            {sowName ? (
              <>AI-generated configuration suggestions for <strong>{sowName}</strong>.</>
            ) : (
              <>AI-generated configuration suggestions for this SOW.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {analyzing && (
            <div className="flex items-center gap-3 py-8 justify-center text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span>Analyzing SOW scope…</span>
            </div>
          )}

          {!analyzing && error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Analysis failed.</strong> {error}
              </AlertDescription>
            </Alert>
          )}

          {!analyzing &&
            !error &&
            newItemSuggestions.length === 0 &&
            enabledExistingIndices.length === 0 && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  No new recommendations. This SOW's scope is already covered by
                  the current configuration.
                </AlertDescription>
              </Alert>
            )}

          {!analyzing && !error && newItemSuggestions.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="size-4 text-purple-600" />
                    New Configuration Items Suggested
                    <Badge variant="secondary">
                      {selectedNewItems.length}/{newItemSuggestions.length} selected
                    </Badge>
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setSelectedNewItems(
                        selectedNewItems.length === newItemSuggestions.length
                          ? []
                          : newItemSuggestions.map((_, i) => i),
                      )
                    }
                  >
                    {selectedNewItems.length === newItemSuggestions.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </div>
                <CardDescription>
                  Requirements AI detected in the SOW that aren't covered by
                  existing configuration items.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {newItemSuggestions.map((item, index) => (
                  <Card key={index} className="bg-blue-50 border-blue-100">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedNewItems.includes(index)}
                          onCheckedChange={() => toggleNewItem(index)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{item.name}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {item.description}
                              </p>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Badge variant="outline">Phase {item.phase}</Badge>
                              <Badge variant="outline">{item.category}</Badge>
                            </div>
                          </div>
                          <Alert className="mt-3 bg-white">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-sm">
                              <strong>Why:</strong> {item.reason}
                            </AlertDescription>
                          </Alert>
                          {item.estimatedHours > 0 && (
                            <p className="text-sm text-muted-foreground mt-2">
                              Estimated: {item.estimatedHours} hours
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}

          {!analyzing && !error && enabledExistingIndices.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-green-600" />
                    Existing Items to Enable
                    <Badge variant="secondary">
                      {selectedExistingItems.length}/{enabledExistingIndices.length} selected
                    </Badge>
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setSelectedExistingItems(
                        selectedExistingItems.length === enabledExistingIndices.length
                          ? []
                          : enabledExistingIndices,
                      )
                    }
                  >
                    {selectedExistingItems.length === enabledExistingIndices.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </div>
                <CardDescription>
                  Existing config items AI recommends enabling for this SOW.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {enabledExistingIndices.map((index) => {
                  const suggestion = existingItemSuggestions[index];
                  return (
                    <div
                      key={suggestion.itemId}
                      className="flex items-start gap-3 p-3 border rounded-md"
                    >
                      <Checkbox
                        checked={selectedExistingItems.includes(index)}
                        onCheckedChange={() => toggleExistingItem(index)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{suggestion.itemId}</span>
                          <Badge variant="outline">Phase {suggestion.phase}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {suggestion.reason}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Close
          </Button>
          {!analyzing && !error && totalSelected > 0 && (
            <Button onClick={handleApply} disabled={applying}>
              {applying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>Apply Selected ({totalSelected})</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
